import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  HedgeBeaconDailyUpdate,
  useUpdateHedgeBeaconDailyUpdate,
} from "@/lib/api/hedgeBeaconDailyUpdates";
import {
  DailyUpdateForm,
  DailyUpdateFormValues,
  validateDailyUpdateFormValues,
} from "./DailyUpdateForm";

type UpdateDailyUpdateModalProps = {
  open: boolean;
  dailyUpdate: HedgeBeaconDailyUpdate | undefined;
  onOpenChange: (open: boolean) => void;
};

export function UpdateDailyUpdateModal({
  open,
  dailyUpdate,
  onOpenChange,
}: UpdateDailyUpdateModalProps) {
  const { toast } = useToast();
  const updateDailyUpdate = useUpdateHedgeBeaconDailyUpdate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (values: DailyUpdateFormValues) => {
    if (isSubmitting || !dailyUpdate) return;

    const validationMessage = validateDailyUpdateFormValues(values);
    if (validationMessage) {
      toast({
        title: "Error",
        description: validationMessage,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await updateDailyUpdate({
        id: dailyUpdate.id,
        date: values.date,
        url: values.url.trim(),
      });

      if (result.success) {
        toast({
          title: "Updated",
          description: "",
        });
        onOpenChange(false);
      } else {
        toast({
          title: "Failed",
          description: result.message || "Failed to update daily update",
        });
      }
    } catch (_error: unknown) {
      toast({
        title: "Error",
        description: "Failed to update daily update",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Daily Update</DialogTitle>
          <DialogDescription>
            Replace the YouTube Daily Update link or date.
          </DialogDescription>
        </DialogHeader>
        {dailyUpdate ? (
          <DailyUpdateForm
            initialValues={{ date: dailyUpdate.date, url: dailyUpdate.url }}
            submitLabel="Update Daily Update"
            submittingLabel="Saving..."
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
